import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import SvgIconThreeDots from '@/components/atoms/SvgIconThreeDots';
import colors from '@/theme/colors';
import { extra, text } from '@/theme/colors/palettes';
import { TUserActivationToggle } from '@/types/User.types';
import {
  Center,
  HStack,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Select,
  Textarea,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useUserDeactivation } from '@/hooks/user/users';
import queryKeys from '@/constants/queryKeys';
import CreateNotificationModal from '@/components/molecules/CreateNotificationModal';
import useSendNotificationToUsers from '@/hooks/notifications/useSendNotificationToUsers';
import usePromoteToOrganizationAdmin from '@/hooks/user/usePromoteToOrganizationAdmin';
import PeerSupportUpgradeModal from './PeerSupportUpgradeModal';
import { NO_ID } from '@/constants/stringVars';
import usePeerSupportLeadAssign from '@/hooks/peer-support/peerSupportLeadAssign';
import usePeerSupportLeadRevoke from '@/hooks/peer-support/peerSupportLeadRevoke';
import useDowngradePeerSupportToMember from '@/hooks/peer-support/downgradePeerSupportToMember';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import { useReinviteUser } from '@/hooks/organization/organization';

import React, { useCallback, useState } from 'react';
import { canReinvite, getReinviteBlockedReason } from '@/utils/invitationDisplay';
import { Text } from '@chakra-ui/react';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { revokeInvitationPost, resolveInvitationRevokePost } from '@/services/api/requests/organization';
import { canRevoke, getRevokeBlockedReason, resolutionBlockedReasonLabels } from '@/utils/invitationDisplay';
import { getInvitationRevokeReason, revokeReasonMessages } from '@/utils/invitationRevokeError';
import { useAuthContext } from '@/context/auth/useAuthContext';
import useToast from '@/hooks/useToast';
import type { ResolveInvitationRevokeDto } from '@/types/generated/schemas';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import type { TCustomAxiosPaginationResponse } from '@/services/api';
import type { TUser } from '@/types/User.types';

const MAX_WIDTH = '240px';

type RosterPopoverActions =
  | 'deactivate'
  | 'reinvite'
  | 'revokeInvitation'
  | 'sendNotification'
  | 'promoteToOrgAdmin'
  | 'upgradeToPeerSupport'
  | 'upgradeToPeerSupportLead'
  | 'downgradeFromPeerSupport'
  | 'downgradeFromPeerSupportLead';

type Props = {
  selectedUser: TUserActivationToggle;
  disabled?: boolean;
  disabledExplanation?: string;
  refetch: () => void;
};

const RosterActionsPopover = ({ selectedUser, disabled, disabledExplanation, refetch }: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const organizationId = [selectedUser.app_metadata?.organizationId, selectedUser.organizationId].find(
    (id) => id && id !== NO_ID,
  );

  const showReinviteOption = Boolean(organizationId) && canReinvite(selectedUser);
  const showRevokeOption = Boolean(organizationId) && canRevoke(selectedUser);
  const revokeBlockedReason = !showRevokeOption ? getRevokeBlockedReason(selectedUser) : undefined;
  const reinviteBlockedReason =
    selectedUser.invitation_status === 'PENDING' && !showReinviteOption
      ? getReinviteBlockedReason(selectedUser)
      : undefined;
  const showSendNotification = selectedUser.invitation_status === 'ACTIVE';
  const isAccepted =
    selectedUser.invitation_status === 'ACTIVE' &&
    (!selectedUser.revoke_state || selectedUser.resolution?.state === 'DONE');
  const isSupportRow =
    selectedUser.resolution?.state !== 'DONE' &&
    (selectedUser.revoke_state === 'NEEDS_REPAIR' || Boolean(selectedUser.resolution));
  const { isSuperAdmin } = useAuthContext();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [decision, setDecision] = useState<ResolveInvitationRevokeDto['decision'] | ''>('');
  const [resolutionReason, setResolutionReason] = useState('');
  const [actionNote, setActionNote] = useState('');

  const refreshRosters = () => {
    refetch();
    void queryClient.invalidateQueries({ queryKey: [queryKeys.users.activatedUsers] });
    void queryClient.invalidateQueries({ queryKey: [queryKeys.organization.organizationRoster] });
  };

  const handleRevokeError = (error: unknown) => {
    const reason = getInvitationRevokeReason(error);
    if (reason === 'ACCOUNT_OPERATION_IN_PROGRESS' || reason === 'MAINTENANCE_FREEZE') {
      // The shared API client presents these account-wide refusals on every screen.
      return;
    }
    if (
      reason === 'ALREADY_ACCEPTED' ||
      reason === 'NEEDS_REPAIR' ||
      reason === 'RESOLUTION_CONFLICT' ||
      reason === 'REVOKE_UNFINISHED'
    )
      refreshRosters();
    const message = reason ? revokeReasonMessages[reason] : 'The invitation could not be revoked. Please try again.';
    if (message) {
      setActionNote(message);
      toast({ status: 'error', title: 'Invitation action', description: message });
    }
  };

  const { mutate: revokeInvitation, isPending: isRevoking } = useMutation({
    mutationFn: () => {
      if (!organizationId) throw new Error('Organization is required to revoke an invitation.');
      return revokeInvitationPost(organizationId, selectedUser.id);
    },
    onSuccess: (result) => {
      setIsRevokeModalOpen(false);
      setActionNote(result.outcome === 'REVOKED' ? 'Invitation revoked.' : 'Invitation resolved.');
      if (result.outcome === 'REVOKED') {
        const removeFromCache = (queryKey: string[]) =>
          queryClient.setQueriesData<InfiniteData<TCustomAxiosPaginationResponse<TUser[]>>>({ queryKey }, (current) => {
            if (!current) return current;
            const hadUser = current.pages.some((page) => page.data.some((user) => user.user_id === selectedUser.id));
            return {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                data: page.data.filter((user) => user.user_id !== selectedUser.id),
                pagination: {
                  ...page.pagination,
                  totalItems: page.pagination.totalItems - (hadUser ? 1 : 0),
                },
              })),
            };
          });
        removeFromCache([queryKeys.users.activatedUsers]);
        removeFromCache([queryKeys.organization.organizationRoster]);
      }
      refreshRosters();
    },
    onError: handleRevokeError,
  });

  const { mutate: resolveInvitation, isPending: isResolving } = useMutation({
    mutationFn: ({
      chosenDecision,
      reason,
    }: {
      chosenDecision: ResolveInvitationRevokeDto['decision'];
      reason: string;
    }) => {
      if (!organizationId) throw new Error('Organization is required to resolve an invitation.');
      return resolveInvitationRevokePost(organizationId, selectedUser.id, chosenDecision, reason);
    },
    onSuccess: () => {
      setIsResolveModalOpen(false);
      setDecision('');
      setResolutionReason('');
      refreshRosters();
    },
    onError: handleRevokeError,
  });

  const recordedDecision = selectedUser.resolution?.decision;
  const availableDecisions =
    selectedUser.allowed_decisions?.filter((item) =>
      recordedDecision
        ? item === recordedDecision || (selectedUser.resolution?.state === 'BLOCKED' && item === 'REMOVE')
        : true,
    ) ?? [];

  const isOrgAdmin = selectedUser.roles?.includes('organizationadmin') === true;
  const isPeerSupport = selectedUser.roles?.includes('peersupport') === true;
  const isPeerSupportLead = selectedUser.roles?.includes('peersupportlead') === true;
  const isMemberCare = selectedUser.roles?.includes('membercare') === true;
  const isContentCreator = selectedUser.roles?.includes('contentcreator') === true;

  const showPromoteToOrgAdmin = !isOrgAdmin && !isPeerSupportLead && !isMemberCare && !isContentCreator;

  const showPeerSupportLeadActions = isPeerSupport && !isOrgAdmin;

  const [isReinviteModalOpen, setIsReinviteModalOpen] = useState(false);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [isPromoteToOrgAdminModalOpen, setIsPromoteToOrgAdminModalOpen] = useState(false);

  const {
    isOpen: isSendNotificationModalOpen,
    onOpen: onSendNotificationModalOpen,
    onClose: onSendNotificationModalClose,
  } = useDisclosure();

  const { mutate: deactivate, isPending: isPendingDeactivation } = useUserDeactivation();
  const { mutate: reinvite, isPending: isReinviting } = useReinviteUser();
  const { mutate: sendNotification, isPending: isSendingNotification } = useSendNotificationToUsers(() => {
    onSendNotificationModalClose();
  });
  const { mutate: promoteToOrgAdmin, isPending: isPromotingToOrgAdmin } = usePromoteToOrganizationAdmin(() => {
    setIsPromoteToOrgAdminModalOpen(false);
    refetch();
  });

  const onConfirmDeactivate = () => {
    if (selectedUser) {
      deactivate(
        { userId: selectedUser.id, hasLastLogin: selectedUser.last_login !== '' },
        {
          onSuccess: () => {
            setIsActivationModalOpen(false);
            refetch();
          },
        },
      );
    }
  };

  const onConfirmReinvite = () => {
    if (organizationId) {
      reinvite(
        {
          userId: selectedUser.id,
          organizationId,
          queryToInvalidateOnSuccess: queryKeys.users.activatedUsers,
        },
        {
          onSuccess: () => {
            setIsReinviteModalOpen(false);
            refetch();
          },
        },
      );
    }
  };

  const [isUpgradeToPeerSupportModalOpen, setIsUpgradeToPeerSupportModalOpen] = useState(false);
  const [isUpgradeToPeerSupportLeadModalOpen, setIsUpgradeToPeerSupportLeadModalOpen] = useState(false);

  const [isDowngradeFromPeerSupportModalOpen, setIsDowngradeFromPeerSupportModalOpen] = useState(false);
  const [isDowngradeFromPeerSupportLeadModalOpen, setIsDowngradeFromPeerSupportLeadModalOpen] = useState(false);

  const { mutate: downgradePeerSupportToMemberMutate, isPending: isDowngradePending } = useDowngradePeerSupportToMember(
    () => {
      refetch();
      setIsDowngradeFromPeerSupportModalOpen(false);
    },
  );

  const downgradePeerSupportToMember = useCallback(() => {
    if (!organizationId) return;
    downgradePeerSupportToMemberMutate({
      organizationId,
      userId: selectedUser.id,
    });
  }, [downgradePeerSupportToMemberMutate, organizationId, selectedUser.id]);

  const { mutate: assignPeerSupportLead, isPending: isPendingPeerSupportLeadAssign } = usePeerSupportLeadAssign(() => {
    refetch();
    setIsUpgradeToPeerSupportLeadModalOpen(false);
  });
  const { mutate: revokePeerSupportLead, isPending: isPendingPeerSupportLeadRevoke } = usePeerSupportLeadRevoke(() => {
    refetch();
    setIsDowngradeFromPeerSupportLeadModalOpen(false);
  });

  const onActionClick = (action: RosterPopoverActions) => {
    switch (action) {
      case 'deactivate':
        setIsActivationModalOpen(true);
        break;
      case 'reinvite':
        setIsReinviteModalOpen(true);
        break;
      case 'revokeInvitation':
        setIsRevokeModalOpen(true);
        break;
      case 'sendNotification':
        onSendNotificationModalOpen();
        break;
      case 'promoteToOrgAdmin':
        setIsPromoteToOrgAdminModalOpen(true);
        break;
      case 'upgradeToPeerSupport':
        setIsUpgradeToPeerSupportModalOpen(true);
        break;
      case 'upgradeToPeerSupportLead':
        setIsUpgradeToPeerSupportLeadModalOpen(true);
        break;
      case 'downgradeFromPeerSupport':
        setIsDowngradeFromPeerSupportModalOpen(true);
        break;
      case 'downgradeFromPeerSupportLead':
        setIsDowngradeFromPeerSupportLeadModalOpen(true);
        break;
      default:
        break;
    }
  };

  const onDeactivate = () => onActionClick('deactivate');
  const onReinvite = () => onActionClick('reinvite');
  const onRevokeInvitation = () => onActionClick('revokeInvitation');
  const onSendNotification = () => onActionClick('sendNotification');
  const onPromoteToOrgAdmin = () => onActionClick('promoteToOrgAdmin');
  const onUpgradeToPeerSupport = () => onActionClick('upgradeToPeerSupport');
  const onUpgradeToPeerSupportLead = () => onActionClick('upgradeToPeerSupportLead');
  const onDowngradeFromPeerSupport = () => onActionClick('downgradeFromPeerSupport');
  const onDowngradeFromPeerSupportLead = () => onActionClick('downgradeFromPeerSupportLead');

  return (
    <VStack>
      <Popover
        placement={'auto'}
        isOpen={isOpen}
        onOpen={disabled && !isSupportRow && !showRevokeOption && !showReinviteOption ? undefined : onOpen}
        onClose={onClose}
      >
        <Tooltip label={disabled && !isSupportRow ? disabledExplanation : undefined}>
          <HStack padding={'8px'} opacity={disabled ? 0.5 : 1}>
            <PopoverAnchor>
              <HStack width={'4px'} height={'15px'} />
            </PopoverAnchor>
            <PopoverTrigger key={selectedUser.id}>
              <Center width={'35px'} marginLeft={'-10px'} cursor={'pointer'}>
                <SvgIconThreeDots color={text.mediumBlue} />
              </Center>
            </PopoverTrigger>
          </HStack>
        </Tooltip>

        <PopoverContent
          bg="white"
          border={0}
          padding={'10px'}
          borderRadius={'8px'}
          boxShadow={'0px 6px 12px 0px #0E006224'}
          // width={'136px'}
          maxWidth={MAX_WIDTH}
          alignItems={'center'}
          // Rendered inside a table cell, which sets `white-space: nowrap` on its
          // content; without this the informational notes overflow (MYO-2767).
          whiteSpace={'normal'}
          overflowWrap={'anywhere'}
        >
          {isAccepted && !disabled && (
            <CustomButton
              isTransparent
              label={'Deactivate'}
              hoverBackgroundColor="extra.red"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.userDeactivate}
              onClick={() => onDeactivate()}
              height={'35px'}
              noShadow
            />
          )}
          {/* Blocked actions stay in the menu, disabled, with the reason as a tooltip. */}
          {(showReinviteOption || reinviteBlockedReason) && (
            <CustomButton
              isTransparent
              label={'Reinvite'}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.email}
              onClick={() => onReinvite()}
              disabled={!showReinviteOption}
              disabledExplanation={reinviteBlockedReason}
              disabledColor="transparent"
              height={'35px'}
              noShadow
            />
          )}
          {(showRevokeOption || revokeBlockedReason) && (
            <CustomButton
              isTransparent
              label={selectedUser.revoke_state === 'UNFINISHED' ? 'Retry revoke' : 'Revoke invitation'}
              hoverBackgroundColor="extra.red"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              onClick={onRevokeInvitation}
              disabled={!showRevokeOption}
              disabledExplanation={revokeBlockedReason}
              disabledColor="transparent"
              height="35px"
              noShadow
            />
          )}
          {organizationId && isSupportRow && isSuperAdmin && availableDecisions.length > 0 && (
            <CustomButton
              isTransparent
              label="Resolve"
              onClick={() => setIsResolveModalOpen(true)}
              height="35px"
              noShadow
            />
          )}
          {selectedUser.resolution && (
            <Text padding="8px" fontSize="sm">
              Recorded decision: {selectedUser.resolution.decision.toLowerCase()}. State:{' '}
              {selectedUser.resolution.state.toLowerCase()}.
            </Text>
          )}
          {selectedUser.resolution?.state === 'BLOCKED' && selectedUser.resolution.blocked_reason && (
            <Text padding="8px" fontSize="sm">
              {resolutionBlockedReasonLabels[selectedUser.resolution.blocked_reason]}
            </Text>
          )}
          {actionNote && (
            <Text padding="8px" fontSize="sm">
              {actionNote}
            </Text>
          )}
          {showSendNotification && !isSupportRow && !disabled && (
            <CustomButton
              isTransparent
              label={'Send Notification'}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.bell}
              onClick={() => onSendNotification()}
              height={'35px'}
              noShadow
            />
          )}
          {showPromoteToOrgAdmin && !isSupportRow && !disabled && (
            <CustomButton
              isTransparent
              label={'Promote to Org Admin'}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.infoCircle}
              onClick={() => onPromoteToOrgAdmin?.()}
              height={'35px'}
              width={MAX_WIDTH}
              noShadow
            />
          )}
          {organizationId && !isSupportRow && !disabled && (
            <CustomButton
              isTransparent
              label={`${isPeerSupport ? 'Downgrade from' : 'Upgrade to'} Peer Support`}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.peerSupport}
              iconBeforeLabelSvgColor={isPeerSupport ? extra.red : text.mediumBlue}
              onClick={() => (isPeerSupport ? onDowngradeFromPeerSupport?.() : onUpgradeToPeerSupport?.())}
              height={'35px'}
              width={MAX_WIDTH}
              noShadow
            />
          )}
          {organizationId && showPeerSupportLeadActions && !isSupportRow && !disabled && (
            <CustomButton
              isTransparent
              label={`${isPeerSupportLead ? 'Downgrade from' : 'Upgrade to'} PS Lead`}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              iconBeforeLabelSvgPath={svgIconPaths.peerSupportShield}
              iconBeforeLabelSvgColor={isPeerSupportLead ? extra.red : text.mediumBlue}
              iconBeforeLabelSvgSize={25}
              onClick={() => (isPeerSupportLead ? onDowngradeFromPeerSupportLead?.() : onUpgradeToPeerSupportLead?.())}
              height={'35px'}
              width={MAX_WIDTH}
              noShadow
            />
          )}
        </PopoverContent>

        <ConfirmationModal
          isOpen={isActivationModalOpen}
          onCancel={() => {
            setIsActivationModalOpen(false);
          }}
          title={'Confirm Deactivation'}
          confirmButtonText={'Deactivate'}
          description={`This will deactivate user ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          onConfirm={onConfirmDeactivate}
          confirmButtonColor={'extra.red'}
          confirmLoading={isPendingDeactivation}
        />
        <ConfirmationModal
          isOpen={isReinviteModalOpen}
          onCancel={() => {
            setIsReinviteModalOpen(false);
          }}
          title="Confirm Reinvite"
          confirmButtonText="Reinvite"
          description={`This will send reinvite email to the user ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          onConfirm={onConfirmReinvite}
          confirmButtonColor="primary.500"
          confirmLoading={isReinviting}
        />
        <ConfirmationModal
          isOpen={isRevokeModalOpen}
          onCancel={() => setIsRevokeModalOpen(false)}
          title={selectedUser.revoke_state === 'UNFINISHED' ? 'Retry revoke' : 'Revoke invitation'}
          confirmButtonText={selectedUser.revoke_state === 'UNFINISHED' ? 'Retry revoke' : 'Revoke invitation'}
          description={`Withdraw the invitation for ${selectedUser.firstName} ${selectedUser.lastName}? Their unaccepted account will be removed.`}
          onConfirm={() => organizationId && revokeInvitation()}
          confirmButtonColor="extra.red"
          confirmLoading={isRevoking}
        />
        <ModalContainer
          isOpen={isResolveModalOpen}
          onClose={() => setIsResolveModalOpen(false)}
          title="Resolve invitation"
        >
          <VStack width="100%" alignItems="stretch" spacing="12px">
            <Text>
              Resolve invitation for {selectedUser.firstName} {selectedUser.lastName}
            </Text>
            <Select
              placeholder="Choose a decision"
              value={decision}
              onChange={(event) => setDecision(event.target.value as ResolveInvitationRevokeDto['decision'])}
            >
              {availableDecisions.map((item) => (
                <option key={item} value={item}>
                  {item === 'ACCEPTED'
                    ? 'Mark accepted'
                    : item === 'REMOVE'
                      ? 'Remove account'
                      : 'Sanitize and reissue'}
                </option>
              ))}
            </Select>
            <Textarea
              placeholder="Reason (required for audit)"
              value={resolutionReason}
              onChange={(event) => setResolutionReason(event.target.value)}
            />
            <HStack>
              <CustomButton label="Cancel" onClick={() => setIsResolveModalOpen(false)} />
              <CustomButton
                label="Confirm resolution"
                disabled={!decision || !resolutionReason.trim()}
                isLoading={isResolving}
                onClick={() =>
                  organizationId && decision && resolveInvitation({ chosenDecision: decision, reason: resolutionReason.trim() })
                }
              />
            </HStack>
          </VStack>
        </ModalContainer>
        <CreateNotificationModal
          isOpen={isSendNotificationModalOpen}
          onClose={onSendNotificationModalClose}
          isLoading={isSendingNotification}
          title={`Send Notification to ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          onSubmit={(form) => {
            if (selectedUser) {
              sendNotification({ form, userIds: [selectedUser.id] });
            }
          }}
        />
        <ConfirmationModal
          isOpen={isPromoteToOrgAdminModalOpen && showPromoteToOrgAdmin}
          onCancel={() => {
            setIsPromoteToOrgAdminModalOpen(false);
          }}
          title="Confirm Promotion"
          confirmButtonText="Promote"
          description={`This will promote user ${selectedUser?.firstName} ${selectedUser?.lastName} to Organization Admin`}
          onConfirm={() => {
            if (selectedUser && showPromoteToOrgAdmin) {
              promoteToOrgAdmin(selectedUser.id);
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isPromotingToOrgAdmin}
        />

        {/* Peer Support modals */}
        {organizationId && (
          <PeerSupportUpgradeModal
            itemToUpgradeToPeerSupportName={`${selectedUser.firstName} ${selectedUser.lastName}`}
            organizationId={organizationId}
            peerSupportModalVisible={isUpgradeToPeerSupportModalOpen}
            setPeerSupportModalVisible={setIsUpgradeToPeerSupportModalOpen}
            userToUpgradeToPeerSupportId={selectedUser.id}
            refetch={refetch}
          />
        )}

        <ConfirmationModal
          isOpen={isDowngradeFromPeerSupportModalOpen}
          onCancel={() => {
            setIsDowngradeFromPeerSupportModalOpen(false);
          }}
          title="Confirm Downgrade from Peer Support"
          confirmButtonText="Downgrade"
          description={`This will downgrade user ${selectedUser?.firstName} ${selectedUser?.lastName} from Peer Support`}
          onConfirm={() => {
            if (selectedUser) {
              downgradePeerSupportToMember();
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isDowngradePending}
        />

        {/* Peer Support Lead modals  */}
        <ConfirmationModal
          isOpen={isUpgradeToPeerSupportLeadModalOpen}
          onCancel={() => {
            setIsUpgradeToPeerSupportLeadModalOpen(false);
          }}
          title="Confirm Upgrade"
          confirmButtonText="Upgrade"
          description={`This will upgrade user ${selectedUser?.firstName} ${selectedUser?.lastName} to Peer Support Lead`}
          onConfirm={() => {
            if (organizationId) {
              assignPeerSupportLead({
                organizationId,
                userId: selectedUser.id,
              });
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isPendingPeerSupportLeadAssign}
        />
        <ConfirmationModal
          isOpen={isDowngradeFromPeerSupportLeadModalOpen}
          onCancel={() => {
            setIsDowngradeFromPeerSupportLeadModalOpen(false);
          }}
          title="Confirm Downgrade from PS Lead"
          confirmButtonText="Downgrade"
          description={`This will downgrade user ${selectedUser?.firstName} ${selectedUser?.lastName} from Peer Support Lead`}
          onConfirm={() => {
            if (organizationId) {
              revokePeerSupportLead({
                organizationId,
                userId: selectedUser.id,
              });
            }
          }}
          confirmButtonColor="primary.500"
          confirmLoading={isPendingPeerSupportLeadRevoke}
        />
      </Popover>
    </VStack>
  );
};

export default RosterActionsPopover;
